import { isAeoAllowed } from "@redotech/redo-aeo-common/is-aeo-allowed";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-inventory-common/views/default-catalog-products-views";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { defaultChildRoute } from "./legacy-redirects";

const catalogProductsViewBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();

const HidePaddingRoutes = [
  "dashboard",
  "prompts",
  "assistant",
  "competitors",
  "brand",
];
function shouldHidePadding(url: string) {
  return HidePaddingRoutes.some((path) => url.includes(path));
}

function aeoEnabledPermission({
  team,
}: {
  team: {
    settings: {
      aeo?: {
        enabled?: boolean;
        subscriptionBilling?: { enabled?: boolean };
        freeTrialEndDate?: Date | null;
      };
    };
  };
}): boolean {
  return isAeoAllowed(team.settings.aeo);
}

export const aeoRoutes: PermissionedRouteObject[] = [
  {
    path: "",
    handle: { breadcrumb: "AEO", permission: aeoEnabledPermission },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={shouldHidePadding} /> };
    },
    children: [
      defaultChildRoute("dashboard"),
      {
        path: "dashboard",
        handle: { breadcrumb: "Dashboard" },
        async lazy() {
          const { AeoDashboard } =
            await import("../../../aeo/dashboard/dashboard");
          return { Component: AeoDashboard };
        },
      },
      {
        path: "prompts",
        handle: { breadcrumb: "Prompts" },
        children: [
          {
            index: true,
            async lazy() {
              const { AeoPromptsPage } =
                await import("../../../aeo/prompt/prompts-page");
              return { Component: AeoPromptsPage };
            },
          },
          {
            path: ":promptId",
            handle: { breadcrumb: "Prompt Details" },
            async lazy() {
              const { PromptResponsesLayout } =
                await import("../../../aeo/prompt-response/prompt-responses-layout");
              return { Component: PromptResponsesLayout };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PromptResponsesPage } =
                    await import("../../../aeo/prompt-response/prompt-responses-page");
                  return { Component: PromptResponsesPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "competitors",
        handle: { breadcrumb: "Competitors" },
        async lazy() {
          const { AeoCompetitorsPage } =
            await import("../../../aeo/competitors/competitors-page");
          return { Component: AeoCompetitorsPage };
        },
      },
      {
        path: "brand",
        handle: { breadcrumb: "Brand" },
        async lazy() {
          const { AeoBrandOverviewPage } =
            await import("../../../aeo/brand-overview-page");
          return { Component: AeoBrandOverviewPage };
        },
      },
      {
        path: "integrations",
        handle: { breadcrumb: "Integrations" },
        async lazy() {
          const { AeoIntegrationsPage } =
            await import("../../../aeo/integrations/integrations-page");
          return { Component: AeoIntegrationsPage };
        },
      },
      {
        path: "assistant/:threadId?",
        handle: { breadcrumb: "Assistant" },
        async lazy() {
          const { AeoAssistantPage } =
            await import("../../../aeo/aeo-assistant/aeo-assistant-page");
          return { Component: AeoAssistantPage };
        },
      },
      {
        path: "catalog",
        handle: { breadcrumb: "Catalog" },
        children: [
          defaultChildRoute("products"),
          {
            path: "products",
            handle: { breadcrumb: "Products" },
            children: [
              defaultChildRoute(DEFAULT_CATALOG_VIEW),
              {
                path: ":viewName",
                handle: { breadcrumb: catalogProductsViewBreadcrumb },
                async lazy() {
                  const { CatalogProductsWrapper } =
                    await import("@redotech/redo-merchant-inventory-app/catalog/catalog-products-wrapper");
                  return {
                    element: (
                      <CatalogProductsWrapper
                        breadcrumb={catalogProductsViewBreadcrumb}
                        productBreadcrumb={productDetailBreadcrumb}
                        productFamilyBreadcrumb={productFamilyDetailBreadcrumb}
                      />
                    ),
                  };
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { CatalogProducts } =
                        await import("@redotech/redo-merchant-inventory-app/catalog/catalog-products");
                      return { Component: CatalogProducts };
                    },
                  },
                  {
                    path: ":productFamilyId",
                    handle: { breadcrumb: productFamilyDetailBreadcrumb },
                    children: [
                      {
                        index: true,
                        async lazy() {
                          const { ProductFamilyView } =
                            await import("@redotech/redo-merchant-inventory-app/product-families/product-family-view");
                          return { Component: ProductFamilyView };
                        },
                      },
                      {
                        path: ":productId",
                        handle: { breadcrumb: productDetailBreadcrumb },
                        children: [
                          {
                            index: true,
                            async lazy() {
                              const { ProductView } =
                                await import("@redotech/redo-merchant-inventory-app/products/product-view");
                              return { Component: ProductView };
                            },
                          },
                          {
                            path: "locations/:locationId",
                            handle: { breadcrumb: "Adjustment History" },
                            async lazy() {
                              const { AdjustmentHistory } =
                                await import("@redotech/redo-merchant-inventory-app/adjustment-history/adjustment-history");
                              return { Component: AdjustmentHistory };
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "optimization",
            handle: { breadcrumb: "Optimization" },
            async lazy() {
              const { ListingOptimizationPage } =
                await import("@redotech/redo-merchant-inventory-app/listing-optimizations/listing-optimization-page");
              return { Component: ListingOptimizationPage };
            },
          },
        ],
      },
    ],
  },
];
